import { useIsMounted } from '@simplywallst/ui-core';
import type { ChangeEvent, ReactNode } from 'react';
import { useRef } from 'react';
import { SliderLabels } from './components/SliderLabels';
import { SliderTextInput } from './components/SliderTextInput';
import type { SliderTrackBandInput } from './utils';
import {
  sliderNormaliseBands,
  sliderTrackBandColor,
  sliderValuePercentage,
} from './utils';
import { parseNumberSafe } from '../../../Narrative/utils';
import { InputRange } from './components/InputRange';

type Props = {
  identifier: string;
  inputId?: string;
  value: number;
  precision: number;
  minRange: number;
  maxRange: number;
  maxManual?: number;
  bands?: SliderTrackBandInput[];
  suffix?: ReactNode;
  onChange: (value: number) => void;
};

export const Slider = ({
  value,
  inputId,
  precision,
  onChange,
  minRange,
  maxRange,
  maxManual,
  identifier,
  suffix,
  bands,
}: Props) => {
  const currentMarkerRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useIsMounted();

  const handleSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(parseNumberSafe(e.target.value, precision));
  };

  const normalisedBands = sliderNormaliseBands(
    bands,
    minRange,
    maxRange,
    'var(--s-color-p-white-170)'
  );
  const sliderPercentage = sliderValuePercentage(value, minRange, maxRange);
  return (
    <div className="pt-x4" ref={containerRef}>
      <SliderTextInput
        id={inputId}
        name={identifier}
        value={value}
        precision={precision}
        suffix={suffix}
        onChange={onChange}
        isValueValid={(newValue) =>
          newValue <= (maxManual ?? maxRange) && newValue >= minRange
        }
        anchorEl={isMounted ? currentMarkerRef.current : null}
        boundaryEl={isMounted ? containerRef.current : null}
      />

      <div className="relative h-x3">
        <div
          ref={currentMarkerRef}
          className="absolute top-0 size-px"
          style={{
            left: `${sliderPercentage}%`,
          }}
        />
        <InputRange
          id={identifier}
          className="absolute inset-0 z-10"
          name={identifier}
          min={minRange}
          max={maxRange}
          step="1"
          value={value}
          onChange={handleSliderChange}
          trackColor={sliderTrackBandColor(normalisedBands)}
        />
      </div>

      <SliderLabels bands={normalisedBands} />
    </div>
  );
};
